import { cn } from '@/lib/cn'
import {
  NativeStackHeaderItem,
  NativeStackHeaderItemButton,
  NativeStackHeaderItemMenu,
  NativeStackNavigationOptions,
} from '@react-navigation/native-stack'
import { useTheme } from '@react-navigation/native'
import React, { cloneElement, useRef, useState } from 'react'
import { Platform, View } from 'react-native'
import SFSymbolesIconsMap from './SFSymbolesIconsMap'
import { Badge, BadgeText } from './ui/badge'
import { Button, ButtonText } from './ui/button'
import { HStack } from './ui/hstack'
import { Image } from './ui/image'
import { Menu, MenuItem, MenuItemLabel } from './ui/menu'

type Options = {
  left?: NativeStackHeaderItem[]
  right?: NativeStackHeaderItem[]
  buttonClassName?: string
}

export default function buildScreenHeaderItems(
  options: Options
):
  | Pick<
      NativeStackNavigationOptions,
      'headerLeft' | 'headerRight' | 'unstable_headerLeftItems' | 'unstable_headerRightItems'
    >
  | undefined {
  if (!options.left?.length && !options.right?.length) return

  if (Platform.OS === 'ios') {
    return {
      unstable_headerLeftItems: options.left?.length ? () => options.left! : undefined,
      unstable_headerRightItems: options.right?.length ? () => options.right! : undefined,
    }
  }

  return {
    headerLeft: options.left?.length
      ? () => <HeaderItems items={options.left!} buttonClassName={options.buttonClassName} />
      : undefined,
    headerRight: options.right?.length
      ? () => <HeaderItems items={options.right!} buttonClassName={options.buttonClassName} className="justify-end" />
      : undefined,
    ...(Platform.OS === 'web' && {
      headerTitleContainerStyle: { flex: 1, flexBasis: '50%' },
      ...(options.left?.length && { headerLeftContainerStyle: { paddingLeft: 16 } }),
      ...(options.right?.length && { headerRightContainerStyle: { paddingRight: 16 } }),
    }),
  }
}

function Icon({ icon, color }: { icon: NonNullable<NativeStackHeaderItemButton['icon']>; color?: string }) {
  if (icon?.type === 'sfSymbol') {
    const IconComponent = SFSymbolesIconsMap[icon?.name]
    return IconComponent ? <IconComponent size={24} color={color} /> : null
  } else if (icon?.type === 'image') {
    return <Image source={icon?.source} />
  }

  return null
}

type HeaderItemsProps = {
  className?: string
  buttonClassName?: string
  items: NativeStackHeaderItem[]
}

function HeaderItems({ className, buttonClassName, items }: HeaderItemsProps) {
  return (
    <HStack space="md" className={className}>
      {items.map((item, index) => (
        <HeaderItemButton key={index} item={item} buttonClassName={buttonClassName} />
      ))}
    </HStack>
  )
}

type HeaderItemProps = {
  item: NativeStackHeaderItem
  buttonClassName?: string
}

function HeaderItemButton({ item, buttonClassName }: HeaderItemProps) {
  const theme = useTheme()

  if (item.type === 'button' || item.type === 'menu') {
    const defaultIconColor = item.variant === 'prominent' ? '#007AFF' : theme.colors.text

    const button = (
      <Button
        className={cn(
          'rounded-full bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent px-2',
          buttonClassName
        )}
        action="secondary"
        onPress={item.type === 'button' ? item.onPress : undefined}
      >
        {item.icon ? (
          <Icon icon={item.icon} color={defaultIconColor} />
        ) : (
          <ButtonText style={item.variant === 'prominent' ? { color: '#007AFF' } : { color: theme.colors.text }}>
            {item.label}
          </ButtonText>
        )}
      </Button>
    )

    return (
      <View className="relative">
        {item.type === 'button' ? button : <HeaderItemMenu item={item} button={button} />}
        {item.badge ? (
          <Badge
            className="absolute -top-3 -right-2 rounded-full bg-red-500 pointer-events-none"
            style={item.badge?.style}
          >
            <BadgeText className="text-white" isTruncated>
              {item.badge?.value}
            </BadgeText>
          </Badge>
        ) : null}
      </View>
    )
  }

  if (item.type === 'custom') return item.element
  if (item.type === 'spacing') return <View style={{ width: item.spacing }} />

  return null
}

type HeaderItemMenuProps = {
  item: NativeStackHeaderItemMenu
  button: React.ReactElement
}

function HeaderItemMenu({ item, button }: HeaderItemMenuProps) {
  const [open, setOpen] = useState(false)
  const [currentMenu, setCurrentMenu] = useState<NativeStackHeaderItemMenu['menu']>(item.menu)

  const isSubmenuTriggered = useRef(false)

  const handleMenuItemPress = (menuItem: NativeStackHeaderItemMenu['menu']['items'][number]) => {
    if (menuItem.type === 'action') return menuItem.onPress?.()

    isSubmenuTriggered.current = true
    setCurrentMenu(item.menu)
  }

  const handleMenuClose = () => {
    if (!isSubmenuTriggered.current) setOpen(false)
    isSubmenuTriggered.current = false
  }

  return (
    <Menu
      trigger={props => cloneElement(button, props)}
      placement="bottom right"
      disabledKeys={['_menuTitle']}
      isOpen={open}
      onOpen={() => setOpen(true)}
      onClose={handleMenuClose}
    >
      {currentMenu.title && (
        <MenuItem key="_menuTitle" textValue={currentMenu.title}>
          <MenuItemLabel size="sm">{currentMenu.title}</MenuItemLabel>
        </MenuItem>
      )}
      {currentMenu.items?.map(menuItem => (
        <MenuItem key={menuItem.label} textValue={menuItem.label} onPress={() => handleMenuItemPress(menuItem)}>
          {menuItem.icon && <Icon icon={menuItem.icon} />}
          <MenuItemLabel>{menuItem.label}</MenuItemLabel>
        </MenuItem>
      ))}
    </Menu>
  )
}
